import { Language, photoExtractionIdFor, StudyMaterialText } from '@quizdb/common';
import { OCR_VERSION } from '../extraction/extractionTypes';
import { PHOTO_TYPES, Photo, PhotoSet } from './photoTypes';

/**
 * Whether `file`'s MIME type is one a photo job accepts (PHO-D3). HEIC/HEIF
 * (`image/heic`, `image/heif`), an empty type and everything else are refused; the caller maps
 * a refusal to `PhotoUnsupported`.
 */
export const isAcceptedPhoto = (file: Blob): boolean =>
  (PHOTO_TYPES as readonly string[]).includes(file.type);

/**
 * `photos` sorted by file name, new array, stable, numeric so `IMG_9.jpg` comes before
 * `IMG_10.jpg` (PHO-D4). The input is not mutated; the caller sorts only the batch just added
 * and appends it after the photos already there.
 */
export const sortPhotosByName = (photos: readonly Photo[]): Photo[] =>
  [...photos].sort((a, b) => a.file.name.localeCompare(b.file.name, undefined, { numeric: true }));

/**
 * Builds what Start uploads for a photo set's extraction (PHO-D5, PHO-D6): the study material
 * text in the set's order, so page N is photo N (QZ-D91), and the extraction id hashed over
 * every photo's `sha256` and `rotation` in order — never over the set's name, which is not
 * content. Its preconditions are the caller's to keep: it throws a plain `Error`, not a
 * `MaterialPrepError`, when the set has no photos, or when any photo's `sha256` or `text` is
 * `undefined`. Start is enabled only when every photo is ready; an empty string is a valid
 * `text`, a blank photo being a page with no text.
 */
export const photoSetExtraction = async (
  set: PhotoSet,
  language: Language,
  hashText: (s: string) => Promise<string>
): Promise<{ id: string; text: StudyMaterialText }> => {
  const { photos } = set;
  if (photos.length === 0) {
    throw new Error('A photo set must have at least one photo.');
  }
  for (const photo of photos) {
    if (photo.sha256 === undefined) {
      throw new Error('Every photo must have a sha256 before its set can be extracted.');
    }
    if (photo.text === undefined) {
      throw new Error('Every photo must have text before its set can be extracted.');
    }
  }

  const text: StudyMaterialText = {
    pdfPageCount: photos.length,
    pages: photos.map((photo, i) => ({ page: i + 1, text: photo.text as string })),
  };

  const setSha256 = await hashText(
    photos.map((photo) => `${photo.sha256}:${photo.rotation}`).join('\n')
  );
  const id = photoExtractionIdFor(setSha256, OCR_VERSION, language);

  return { id, text };
};
